import React from 'react';
import {
  BarChart3,
  Copy,
  FileText,
  FormInput,
  Link2,
  MousePointerClick,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  WORKFLOW_ADD_TRIGGER_PLACEHOLDER_CLASSES,
  WORKFLOW_TRIGGER_CARD_CLASSES,
} from '../../utils/workflowStepCardStyles';
import { getTriggerFilterSummary, getTriggerTitle } from '../../utils/workflowTriggerUtils';

function getTriggerIcon(trigger) {
  const id = String(trigger?.id || trigger?.trigger_type || '').toLowerCase();
  if (id.includes('form')) return FormInput;
  if (id.includes('link')) return Link2;
  if (id.includes('email')) return FileText;
  if (id.includes('password') || id.includes('auth')) return Zap;
  return MousePointerClick;
}

export const WorkflowTriggerCard = ({
  trigger,
  readOnly = false,
  onClick,
  onCopy,
  onDelete,
  onStatsHover,
  onStatsLeave,
  onStatsClick,
  statsOpen = false,
}) => {
  const Icon = getTriggerIcon(trigger);
  const title = getTriggerTitle(trigger);
  const subtitle = getTriggerFilterSummary(trigger);

  return (
    <div
      className={cn(
        'flex w-[340px] flex-col overflow-hidden',
        WORKFLOW_TRIGGER_CARD_CLASSES.card,
        readOnly ? 'opacity-90' : '',
      )}
      data-testid={`trigger-card-${trigger?.id}`}
      data-workflow-canvas-node
    >
      <button
        type="button"
        onClick={() => !readOnly && onClick?.(trigger)}
        disabled={readOnly}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-4 text-left',
          readOnly ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50/80',
        )}
        data-testid="trigger-node"
        data-workflow-canvas-interactive
        data-workflow-editor-ignore-pan
      >
        <div
          className={cn(
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl',
            WORKFLOW_TRIGGER_CARD_CLASSES.icon,
          )}
        >
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className={WORKFLOW_TRIGGER_CARD_CLASSES.title}>{title}</p>
          <p className={cn('mt-1', WORKFLOW_TRIGGER_CARD_CLASSES.subtitle)}>{subtitle}</p>
        </div>
      </button>

      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 text-slate-500',
          WORKFLOW_TRIGGER_CARD_CLASSES.actionRow,
        )}
      >
        {!readOnly && onCopy ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onCopy?.(trigger);
            }}
            data-workflow-card-action
            data-workflow-editor-ignore-pan
            className="rounded p-1.5 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Duplicate trigger"
            data-testid={`trigger-copy-${trigger?.id}`}
          >
            <Copy size={15} />
          </button>
        ) : null}
        {!readOnly && onDelete ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onDelete?.(trigger);
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            data-workflow-card-action
            data-workflow-editor-ignore-pan
            className="rounded p-1.5 hover:bg-red-50 hover:text-red-600"
            aria-label="Delete trigger"
            data-testid={`trigger-delete-${trigger?.id}`}
          >
            <Trash2 size={15} />
          </button>
        ) : null}
        {onStatsClick ? (
          <button
            type="button"
            onMouseEnter={(event) => onStatsHover?.(trigger, event.currentTarget)}
            onMouseLeave={() => onStatsLeave?.(trigger)}
            onClick={(event) => {
              event.stopPropagation();
              onStatsClick?.(trigger, event.currentTarget);
            }}
            data-workflow-card-action
            data-workflow-editor-ignore-pan
            className={cn(
              'ml-auto inline-flex items-center gap-1.5 rounded px-1 py-1 text-sm font-medium text-emerald-600 hover:bg-emerald-50',
              statsOpen && 'bg-emerald-50',
            )}
            data-testid={`trigger-stats-${trigger?.id}`}
          >
            <BarChart3 size={14} />
            Stats
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const WorkflowAddTriggerCard = ({ readOnly = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={readOnly}
    className={cn(
      'flex items-center justify-center gap-3 transition-colors',
      WORKFLOW_ADD_TRIGGER_PLACEHOLDER_CLASSES.card,
      readOnly ? 'cursor-default opacity-80' : 'cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/70',
    )}
    data-testid="add-trigger-placeholder"
    data-workflow-row-control
    data-workflow-editor-ignore-pan
  >
    <div
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-lg',
        WORKFLOW_ADD_TRIGGER_PLACEHOLDER_CLASSES.icon,
      )}
    >
      <Plus size={18} />
    </div>
    <span className={WORKFLOW_ADD_TRIGGER_PLACEHOLDER_CLASSES.title}>Add new trigger</span>
  </button>
);

export const WorkflowTriggerEmptyState = ({ readOnly, onClick, title, subtitle = 'No filters applied' }) => (
  <div className="flex w-full justify-center">
    <WorkflowAddTriggerCard
      readOnly={readOnly}
      onClick={() => !readOnly && onClick?.()}
    />
  </div>
);

export default WorkflowTriggerCard;
